import {
  ColumnDef,
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDebounce } from 'use-debounce';

import { AnnotationQueueCrudPane } from '@/components/AnnotationQueuePopover/AnnotationQueueCrudPane';
import { DataGridColumnVisibilityPopover } from '@/components/DataGrid/DataGridColumnVisibilityPopover';
import { DataGridSearchInput } from '@/components/DataGrid/DataGridSearchInput';
import { ErrorBanner } from '@/components/ErrorBanner';
import { IconButton, Tooltip } from '@/design-system';
import {
  GridPaginationModel,
  useDataGridState,
} from '@/hooks/useDataGridState';
import { usePermissions } from '@/hooks/usePermissions.tsx';
import { useStoredResourceTags } from '@/hooks/useStoredResourceTags/useStoredResourceTags';
import { useAnnotationQueues, useOrganizationId } from '@/hooks/useSwr';
import DetailedTrashIcon from '@/icons/DetailedTrashIcon.svg?react';
import SimpleEditIcon from '@/icons/SimpleEditIcon.svg?react';
import { AnnotationQueueSchema, GetDatasetsQueryParams } from '@/types/schema';
import {
  apiAnnotationQueuesPath,
  appAnnotationQueuesPath,
  appOrganizationPath,
} from '@/utils/constants';
import { localTime } from '@/utils/utc-time';

import { DataGrid, DataGridPagination } from '../../components/DataGrid';
import {
  emulateNativeClick,
  emulateNativeMiddleClick,
  useDataGridSizingLocalStorage,
} from '../../components/DataGrid.utils';
import { DeleteModal } from '../../components/Delete';
import { TableSkeleton } from '../Home/ResourceTable/TableSkeleton';

type SimpleTableProps =
  | {
      simple: true;
      defaultPaginationModel: GridPaginationModel;
    }
  | {
      simple?: false;
      filter: GetDatasetsQueryParams;
    };

type TableProps = {
  emptyState?: React.ReactNode;
} & SimpleTableProps;

const columnHelper = createColumnHelper<AnnotationQueueSchema>();

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const COLUMNS_DEF: ColumnDef<AnnotationQueueSchema, any>[] = [
  columnHelper.accessor('name', {
    header: 'Name',
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <div className="inline-flex flex-col items-start justify-start">
          <div className="text-sm font-normal">{row.original.name}</div>
          <div className="text-sm font-normal text-tertiary">
            {row.original.description}
          </div>
        </div>
      );
    },
  }),
  columnHelper.accessor('total_runs', {
    header: 'Total runs',
    enableSorting: false,
    cell: ({ row }) => {
      return (
        <div className="text-sm font-normal leading-tight text-tertiary">
          {row.original.total_runs}
        </div>
      );
    },
  }),
  columnHelper.accessor('updated_at', {
    header: 'Last updated',
    enableSorting: false,
    cell: ({ row }) => {
      const momentLastUpdated = localTime(row.original.updated_at);
      const lastUpdatedString = momentLastUpdated.fromNow();
      return (
        <div className="text-sm font-normal leading-tight text-tertiary">
          {lastUpdatedString}
        </div>
      );
    },
  }),
  columnHelper.accessor('created_at', {
    header: 'Created',
    enableSorting: false,
    cell: ({ row }) => {
      const momentLastUpdated = localTime(row.original.created_at);
      const lastUpdatedString = momentLastUpdated.fromNow();
      return (
        <div className="text-sm font-normal leading-tight text-tertiary">
          {lastUpdatedString}
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'actions',
    cell: ({ row, table }) => {
      const { actions } = (table.options.meta ?? {}) as {
        actions: {
          deleteEnabled: boolean;
          editEnabled: boolean;
          handleDelete: (id: AnnotationQueueSchema) => void;
          handleEdit: (aq: AnnotationQueueSchema) => void;
        };
      };

      if (!actions?.deleteEnabled && !actions?.editEnabled) return null;

      const disableDelete = !!row.original.run_rule_id;

      return (
        <div className="flex gap-2">
          {actions.deleteEnabled && (
            <Tooltip
              title={
                disableDelete
                  ? 'Cannot delete evaluator annotation queue'
                  : undefined
              }
            >
              <div
                onClick={(e) => {
                  e.stopPropagation();
                }}
              >
                <IconButton
                  disabled={disableDelete}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!disableDelete) {
                      actions.handleDelete(row.original);
                    }
                  }}
                  data-testid="delete-annotation-queue-button"
                  color="secondary"
                  variant="plain"
                  icon={DetailedTrashIcon}
                  label={
                    disableDelete
                      ? 'Cannot delete evaluator annotation queue'
                      : 'Delete queue'
                  }
                />
              </div>
            </Tooltip>
          )}
          {actions.editEnabled && (
            <IconButton
              color="secondary"
              variant="plain"
              onClick={(e) => {
                e.stopPropagation();
                actions.handleEdit(row.original);
              }}
              data-testid="edit-annotation-queue-button"
              icon={SimpleEditIcon}
              label="Edit queue"
            />
          )}
        </div>
      );
    },
    enableSorting: false,
    enableHiding: false,
    enableResizing: false,
    size: 65,
  }),
];

const EMPTY_TABLE = [];

const COLUMN_VISIBILITY_STATE_KEY = 'ls:ui:annotation-queues-table-columns';

export const AnnotationQueuesTable = (props: TableProps) => {
  'use no memo';
  const { simple, emptyState } = props;
  // navigation
  const navigate = useNavigate();

  // pagination
  const {
    paginationModel,
    setPaginationModel,
    resetPaginationModel,
    columnVisibilityModel,
    setColumnVisibilityModel,
  } = useDataGridState({
    columnVisibilityStateKey: COLUMN_VISIBILITY_STATE_KEY,
  });

  const mPaginationModel = simple
    ? props.defaultPaginationModel
    : paginationModel;

  const [name, setName] = useState('');
  const [debounceName] = useDebounce(name, 500, { trailing: true });

  const { selectedTags } = useStoredResourceTags();

  useEffect(() => {
    if (!simple) {
      resetPaginationModel();
    }
    // Reset pagination when search term or tags change
  }, [debounceName, selectedTags, simple, resetPaginationModel]);

  // data fetching
  const {
    data: rows,
    isLoading,
    isValidating,
    error,
  } = useAnnotationQueues({
    ...(simple ? {} : props.filter),
    tag_value_id: selectedTags.map((tag) => tag.tag_value_id),
    name_contains: debounceName ?? undefined,
    offset: mPaginationModel.pageIndex * mPaginationModel.pageSize,
    limit: mPaginationModel.pageSize,
  });

  // columns and actions
  const [deleteOpen, setDeleteOpen] = useState<AnnotationQueueSchema>();
  const [editOpen, setEditOpen] = useState<AnnotationQueueSchema | null>(null);

  const [columnSizing, setColumnSizing] =
    useDataGridSizingLocalStorage('annotationQueues');

  const { authorize } = usePermissions();

  const table = useReactTable({
    columns: COLUMNS_DEF,
    data: rows ?? EMPTY_TABLE,

    state: {
      pagination: mPaginationModel,
      columnSizing,
      columnVisibility: columnVisibilityModel,
    },

    meta: {
      actions: {
        handleDelete: setDeleteOpen,
        handleEdit: setEditOpen,
        deleteEnabled: authorize('annotation-queues:delete'),
        editEnabled: authorize('annotation-queues:update'),
      },
    },
    onPaginationChange: setPaginationModel,
    onColumnSizingChange: setColumnSizing,
    onColumnVisibilityChange: setColumnVisibilityModel,
    getRowId: (row) => row.id,

    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),

    manualExpanding: true,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,

    columnResizeMode: 'onChange',

    pageCount: Math.ceil(
      +(rows?.headers?.['x-pagination-total'] || 0) / mPaginationModel.pageSize
    ),
  });

  const organizationId = useOrganizationId();

  if (error) {
    return <ErrorBanner>{error.message}</ErrorBanner>;
  }
  const renderContent = () => {
    if (isLoading && !rows?.length) {
      return (
        <TableSkeleton
          rows={mPaginationModel.pageSize}
          rowHeight={simple ? 56 : 72}
        />
      );
    }

    if (!rows?.length && !name && emptyState && selectedTags.length === 0) {
      return <>{emptyState}</>;
    }

    return (
      <>
        <div className="min-h-[55px] overflow-hidden rounded-lg border border-secondary">
          <DataGrid
            cellHeight={simple ? 56 : 72}
            cellSpacingVariant="wide"
            table={table}
            isLoading={isLoading || isValidating}
            emptyState={emptyState}
            onClick={(row, event) => {
              const target = `/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${row.id}`;
              if (!emulateNativeClick(target, event.nativeEvent)) {
                navigate(target);
              }
            }}
            onMiddleClick={(row) => {
              const target = `/${appOrganizationPath}/${organizationId}/${appAnnotationQueuesPath}/${row.id}`;
              if (!emulateNativeMiddleClick(target)) {
                navigate(target);
              }
            }}
            stickyRightColumn={true}
          />
        </div>

        {!simple && <DataGridPagination table={table} />}

        <DeleteModal
          endpoint={apiAnnotationQueuesPath}
          id={deleteOpen?.id}
          name={deleteOpen?.name}
          isOpen={!!deleteOpen}
          doClose={() => setDeleteOpen(undefined)}
        />
        <AnnotationQueueCrudPane
          open={!!editOpen}
          closeModal={() => setEditOpen(null)}
          onSuccess={() => {
            setEditOpen(null);
          }}
          existingAnnotationQueue={editOpen ?? undefined}
        />
      </>
    );
  };

  return (
    <>
      {!simple && (
        <div className="mb-2 flex items-stretch justify-between">
          <div className="flex items-stretch gap-2">
            <DataGridSearchInput value={name} onChange={setName} />
          </div>
          <DataGridColumnVisibilityPopover
            table={table}
            localStorageKey={COLUMN_VISIBILITY_STATE_KEY}
          />
        </div>
      )}

      {renderContent()}
    </>
  );
};
